/**
 * What a template needs, read off its own declaration.
 *
 * A template that ships with BroadPaper declares the data source it was designed
 * against — a client name, a period end, a list of line items — and that
 * declaration is the list of things to fill in. Reading the expressions instead
 * would mean understanding them, and then mapping would have to rewrite them,
 * and a mapping step that edits a document is a mapping step that can break one.
 *
 * Nesting is flattened to dotted paths because a Cloud user is never shown one:
 * `client.name` is the key, "Client name" is what they read. Fields inside a
 * list are marked `itemOf`, which is what tells the interface to ask "which
 * column?" rather than "which value?".
 */
import type { DataSource, FieldSchema } from "@broadpaper/core";
import type { Requirement } from "./types.js";

/** How deep to walk. Deeper than this and a template is not one a Cloud user designed. */
const MAX_DEPTH = 4;

export function requirementsOf(source: DataSource): Requirement[] {
  const out: Requirement[] = [];
  walk(source.schema, "", [], out, 0);
  return out;
}

function walk(fields: Record<string, FieldSchema>, prefix: string, labels: string[], out: Requirement[], depth: number): void {
  for (const [key, schema] of Object.entries(fields)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const label = schema.label ?? humanise(key);

    if (schema.type === "object" && schema.fields) {
      if (depth + 1 >= MAX_DEPTH) continue;
      // A group is not itself a requirement — there is nothing to fill in for
      // "Client", only for the fields inside it. Its label joins the path so a
      // question reads "Client › Name" rather than just "Name", which matters on
      // a template with a client and a supplier.
      walk(schema.fields, path, [...labels, label], out, depth + 1);
      continue;
    }

    if (schema.type === "array") {
      out.push(requirement(path, label, labels, schema, true));
      const item = schema.itemSchema;
      if (item?.type === "object" && item.fields && depth + 1 < MAX_DEPTH) {
        for (const [k, f] of Object.entries(item.fields)) {
          out.push({ ...requirement(`${path}.${k}`, f.label ?? humanise(k), [...labels, label], f, false), itemOf: path });
        }
      }
      continue;
    }

    out.push(requirement(path, label, labels, schema, false));
  }
}

function requirement(path: string, label: string, labels: string[], schema: FieldSchema, isArray: boolean): Requirement {
  const r: Requirement = {
    path,
    label: labels.length ? `${labels.join(" › ")} › ${label}` : label,
    type: schema.type,
    // A field the template declares as required is one it cannot render
    // sensibly without; everything else is allowed to be blank, and asking
    // about all of them is what turns two questions into fourteen.
    required: schema.required === true || isArray
  };
  if (schema.itemSchema?.type === "object" && schema.itemSchema.fields) r.itemFields = schema.itemSchema.fields;
  if (schema.example !== undefined) r.example = schema.example;
  if (schema.currency) r.currency = schema.currency;
  if (schema.format) r.format = schema.format;
  return r;
}

/** The leaf label a requirement is shown by, without its group. */
export function leafLabel(requirement: Requirement): string {
  const parts = requirement.label.split(" › ");
  return parts[parts.length - 1] ?? requirement.label;
}

function humanise(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_.-]+/g, " ")
    .trim()
    .split(/\s+/);
  if (!words.length) return key;
  return words[0]![0]!.toUpperCase() + words[0]!.slice(1) + (words.length > 1 ? ` ${words.slice(1).map((w) => w.toLowerCase()).join(" ")}` : "");
}
